import React, { useMemo } from 'react';
import '@/pages/intelligence/assets/scss/TopCharts/MobileGames/components/StoreRank.scss';
import type { IBaseProps, IGameType } from '@/pages/intelligence/types/common';
import RankTrendVisualis from '@/pages/intelligence/pages/TopCharts/PCConsoleGames/components/GamesDetails/StoreRank/RankTrend/components/RankTrendVisualis';
import RankTrendTableContent from '@/pages/intelligence/pages/TopCharts/PCConsoleGames/components/GamesDetails/StoreRank/RankTrend/components/RankTrendTableContent';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import { toDateTypeNor } from '@/pages/intelligence/utils/common';
import { getMarketKeyList } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/utils';
import type { Game } from '@intelligence/pages/TopCharts/PCGames/_config/type';
import useCountriesNames from '@intelligence/hooks/useCountriesNames';
import { sortTableByMetric } from '@/pages/intelligence/utils/tableUtils';
import { useDispatch } from 'umi';

interface IStoreRankDetailsProps extends IBaseProps {
  entityType: IGameType;
  appIds?: any;
  isBasicLoading: boolean;
  basicInfo: object | any;
  activeSegment: string;
  setActiveSegment?: any;
  firstWeekSinceReleaseDates?: string[];
}

interface CountryType {
  code?: string;
  nameEn?: string;
  nameCh?: string;
}
interface ChangeMarketProfileAllParamsType {
  type: 'cancel' | 'all';
}
interface ItemType extends Game {
  market?: string;
}
interface SortByItemType {
  ratioType: 0 | 1;
  sortVal: 'desc' | 'asc';
  sortMetric: string;
  sortSource?: string;
  key?: string;
}

const RankTrend: React.FC<IStoreRankDetailsProps> = (props) => {
  const {
    entityType,
    regionOptions,
    storerankMetricsListCur,
    earliestDayData,
    setEarliestPeriodCur,
    allLoading,
    fetchParams,
    metricSelectedValue,
    setMetricSelectedValue,
    trendLoading,
    trendError,
    tableSelection,
    tableData,
    setTableData,
    getTableData,
    setTableSelection,
    getLineChartData,
    lineData,
    defaultSelectionGames,
    tableSearchValue,
    tableLoading,
    setIsOperateCheckbox,
    rankType,
    setRankType,
    reqDateObject,
    setBoolean,
    setTableSearchValue,
    cacheTableData,
    updateSelection,
    pageSortAttr,
    setPageSortAttr,
    radioType,
    setRadioType,
    combineMarkets,
    firstWeekSinceReleaseDates,
  } = props;

  // console.log('RankTrendRankTrend', props);
  const dispatch = useDispatch();
  const { findCountryObj } = useCountriesNames();
  const sortMetric = useMemo(() => {
    return storerankMetricsListCur[0]?.value;
  }, [storerankMetricsListCur]);

  const handleMetricChangeFun = (value: string) => {
    const earliestDayList = [...earliestDayData]?.filter((item: any) => item.source === value);
    if (earliestDayList?.length === 0) {
      dispatch({ type: `${DBI_TOPCHARTS}/setEarliestCurDay`, payload: '' });
      setEarliestPeriodCur('');
    } else {
      for (const n in earliestDayList) {
        if (toDateTypeNor[fetchParams?.granularity] === toDateTypeNor[earliestDayList[n]?.period]) {
          dispatch({
            type: `${DBI_TOPCHARTS}/setEarliestCurDay`,
            payload: earliestDayList[n]?.date || '',
          });
          setEarliestPeriodCur(earliestDayList[n].date || '');
        }
      }
    }
  };

  const changeMarketProfileAll = (params: ChangeMarketProfileAllParamsType) => {
    if (params.type === 'cancel') {
      setTableSelection([]);
    } else {
      setTableSelection(defaultSelectionGames);
      getLineChartData(getMarketKeyList(defaultSelectionGames));
    }
  };

  const resetPageSortAttr = function () {
    setPageSortAttr({
      currentSortValue: 'desc',
      currentSortMetric: sortMetric,
      currentRatioType: 0,
      sortKey: sortMetric,
    });
  };

  const updateTableField = function (param: string) {
    setTableSearchValue(param);
    if (!param?.length) {
      setTableData([...cacheTableData]);
      return;
    }
    const countryObj: CountryType = findCountryObj(param) || {};
    const { code = '' } = countryObj;
    const newTableData = [...tableData].filter((item: ItemType) => item.market == code);
    setTableData(newTableData);
  };

  const onSortChage = (e: SortByItemType) => {
    // console.log('onSortChage-e', e);
    setPageSortAttr({
      currentSortValue: e.sortVal,
      currentSortMetric: e.sortMetric,
      currentRatioType: e.ratioType,
      sortKey: e.key,
    });
    const isRank = e.ratioType === 0;
    let sortTable = sortTableByMetric({
      tableData,
      sortParams: e,
      isRank,
      growthProperty: 'dod',
    });

    const [globalList, othersList] = sortTable.reduce(
      (acc, dataObj) => {
        if (dataObj?.market === 'global') {
          acc[0].push(dataObj);
        } else {
          acc[1].push(dataObj);
        }
        return acc;
      },
      [[], []],
    );

    sortTable = [...globalList, ...othersList];
    updateSelection(sortTable, true);
    sortTable = sortTable.map((item, index) => ({ ...item, rank: index + 1 }));
    setTableData(sortTable.slice(0));
  };

  // 可视化数据是否有数据排查
  const isLineNodata = useMemo(
    () =>
      !(
        tableSelection?.findIndex(
          (item: any) => Array.isArray(item?.metrics) && item?.metrics?.length > 0,
        ) > -1
      ),
    [tableSelection],
  );

  // 宽表数据是否有数据排查
  const isTableNodata = useMemo(
    () =>
      !(
        tableData?.findIndex(
          (item: any) => Array.isArray(item?.metrics) && item?.metrics?.length > 0,
        ) > -1
      ),
    [tableData],
  );

  return (
    <>
      {/* RankTrend可视化内容 */}
      <RankTrendVisualis
        lineData={lineData}
        loading={allLoading}
        trendLoading={trendLoading}
        trendError={trendError}
        entityType={entityType}
        tableSelection={tableSelection}
        segmentListCur={storerankMetricsListCur}
        filterLoading={allLoading}
        regionOptions={regionOptions}
        isLineNodata={isLineNodata}
        fetchParams={fetchParams}
        segmentValue={metricSelectedValue}
        setSegmentValue={setMetricSelectedValue}
        handleMetricChangeFun={handleMetricChangeFun}
        storerankMetricsListCur={storerankMetricsListCur}
        firstWeekSinceReleaseDates={firstWeekSinceReleaseDates}
      />
      {/* RankTrend宽表内容 */}
      <RankTrendTableContent
        entityType={entityType}
        tableData={tableData}
        getTableData={getTableData}
        tableSelection={tableSelection}
        setTableSelection={setTableSelection}
        changeMarketProfileAll={changeMarketProfileAll}
        selectAllKeys={defaultSelectionGames}
        setTableSearchValue={updateTableField}
        onSortChage={onSortChage}
        pageSortAttr={pageSortAttr}
        tableSearchValue={tableSearchValue}
        resetPageSortAttr={resetPageSortAttr}
        fetchParams={fetchParams}
        tableLoading={tableLoading}
        setIsOperateCheckbox={setIsOperateCheckbox}
        filterLoading={allLoading}
        regionOptions={regionOptions}
        getLineChartData={getLineChartData}
        rankType={rankType}
        setRankType={setRankType}
        isTableNodata={isTableNodata}
        reqDateObject={reqDateObject}
        setBoolean={setBoolean}
        dataSourcesColumns={storerankMetricsListCur}
        radioType={radioType}
        setRadioType={setRadioType}
        combineMarkets={combineMarkets}
      />
    </>
  );
};

export default RankTrend;
